<script setup>
import { inject, computed } from 'vue';
import { useProductsStore } from '@/app/stores/productsStore.js';

const productsStore = useProductsStore();

const config = inject('config');
const gaPush = inject('gaPush');
const props = defineProps(['button', 'productName', 'tags', 'gaPrefix', 'category']);

const externalSiteAttributes = {
  target: '_blank',
  rel: 'noopener',
};

// Attributes required to activate modal
const modalAttributes = {
  'data-name': props.productName,
  'data-category-english-name': props.button.dataCategoryEnglishName,
  'data-mrrpprice': props.button.dataMrrpprice,
  'data-ismodalenabled': props.button.dataIsmodalenabled,
  'data-url': props.button.dataUrl,
  ...(props.button.dataIsmodalenabled && externalSiteAttributes),
};

const logic = {
  brochure: {
    title: config.brochuteBtnTitle,
    tag: 'a',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 20"><path d="M26.6 0H2.4C1.4 0 .5.8.5 1.8v16.4c0 1 .8 1.8 1.9 1.8h24.2c1 0 1.9-.8 1.9-1.8V1.8c0-1-.8-1.8-1.9-1.8ZM14 19.1H2.4c-.5 0-1-.4-1-.9V1.8c0-.5.5-1 1-1H14v18.3Zm13.6-.9c0 .5-.4 1-1 1H15V.8h11.6c.6 0 1 .4 1 .9v16.4Z"/></svg>',
    modalAttributes: { ...modalAttributes },
    ...(props.button.dataIsmodalenabled && { class: 'js-modal-brochure' }),
    url: props.button.dataIsmodalenabled ? '#' : props.button.dataUrl,
    gaEvent: props.gaPrefix + 'download_brochure',
  },
  price: {
    title: config.requestPriceBtnTitle,
    tag: 'a',
    icon: '<svg viewBox="0 0 22 21" xmlns="http://www.w3.org/2000/svg" xml:space="preserve" fill-rule="evenodd" clip-rule="evenodd" stroke-linejoin="round" stroke-miterlimit="2"><path d="M16.7.3 3.2 13.8l-2.5 7 7-2.5L21.2 4.8l-.7-.7L7 17.5l-4.8 1.7L4 14.4 17.4 1.1l-.7-.8Z" /></svg>',
    modalAttributes: { ...modalAttributes },
    ...(props.button.dataIsmodalenabled && { class: 'js-modal-quote' }),
    url: props.button.dataIsmodalenabled ? '#' : props.button.dataUrl,
    gaEvent: props.gaPrefix + 'request_price',
  },
  buynow: {
    tag: 'a',
    icon: '<svg viewBox="0 0 35 34" xmlns="http://www.w3.org/2000/svg" xml:space="preserve" fill-rule="evenodd" clip-rule="evenodd" stroke-linejoin="round" stroke-miterlimit="2"><path d="M30.7.8 19.5 0c-.8 0-1.6.2-2.2.8L1 17.2a2.8 2.8 0 0 0 0 4l12 12a2.8 2.8 0 0 0 4 0l16.3-16.4c.6-.6.9-1.4.8-2.2l-.7-11.2A2.8 2.8 0 0 0 30.8.8Zm0 2c.3 0 .7.3.7.7l.7 11.2c0 .3 0 .5-.2.7L15.6 31.7a.8.8 0 0 1-1.2 0l-12-12a.8.8 0 0 1 0-1.1L18.7 2.2l.7-.2 11.2.7Z"/><circle cx="27" cy="7.2" r="2.1"/></svg>',
    gaEvent: props.gaPrefix + 'buy_online',
  },
  cta: {
    tag: 'a',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 24"><path d="m15.2 18-4 4.2A6 6 0 0 1 6.6 24a6 6 0 0 1-4.4-1.8 6 6 0 0 1-1.8-4.4 6 6 0 0 1 1.8-4.4l4.2-4.1.6.6L3 14c-1 1-1.6 2.3-1.6 3.8 0 1.4.5 2.7 1.6 3.7 1 1 2.3 1.6 3.7 1.6 1.5 0 2.7-.5 3.8-1.6l4.1-4.1.6.6ZM9 16.1l-.6-.6L16 7.8l.7.7L9 16Zm9.5-1.4-.6-.6L22 10c1-1 1.6-2.3 1.6-3.8 0-1.4-.5-2.7-1.6-3.7-1-1-2.3-1.6-3.7-1.6-1.5 0-2.7.5-3.8 1.6l-4.1 4.1-.6-.6 4-4.2A6 6 0 0 1 18.4 0a6 6 0 0 1 4.4 1.8 6 6 0 0 1 1.8 4.4 6 6 0 0 1-1.8 4.4l-4.2 4.1Z"/></svg>',
    gaEvent: props.gaPrefix + 'optional_cta',
  },
};

const buttonLogic = logic[props.button.type];
const buttonLabel = buttonLogic.title || props.button.title;
const buttonUrl = buttonLogic.url || props.button.url;
const productCategory = props.category;

function handleClickGA() {
  const gaPayload = {
    event: buttonLogic.gaEvent,
    link_text: buttonLabel,
    link_url: buttonUrl,
    product_name: props.productName,
    product_category: productCategory,
    product_flag1: props.tags.some((tag) => tag.type === 'popular'),
    product_flag2: props.tags.some((tag) => tag.type === 'new'),
    measurement_system: productsStore.system || null,
  };
  gaPush(gaPayload);
}
</script>

<template>
  <component
    class="c-product-card__btn"
    :class="buttonLogic.class"
    :is="buttonLogic.tag"
    :href="buttonUrl"
    v-bind="{ ...buttonLogic.modalAttributes, ...(button.external && externalSiteAttributes) }"
    @click="handleClickGA"
  >
    <span class="c-product-card__btn-icon" v-if="buttonLogic.icon" v-html="buttonLogic.icon"></span>
    <span>{{ buttonLabel }}</span>
  </component>
</template>